/**
 * camera_comfort.ts — ONE scale for every ADDITIVE camera motion on the ground and in vehicles (physics follow-up A,
 * 2026-10-05; `audit-feel.md` G2). PURE apart from a tiny cached store. No three.
 *
 * The sector has had its "Secondary motion" row since space campaign L (System / Full / Reduced / Off, following the
 * OS — `space_comfort.ts`); the ground had none: the trauma shake hid behind a localStorage flag and the kick, the
 * sprint / dash FOV and the chase cameras moved the view for everyone. This is the ground's row:
 *
 *   · `cameraMotion()` — the player's choice, `atlas:camera-motion` (default 'system': follows
 *     `prefers-reduced-motion`, the same media query the sector reads). Options → Controls → Camera, applied LIVE — a
 *     comfort setting is judged by feel, like a sound level, so it is not a drafted Graphics row.
 *   · `cameraMotionScale()` — 0..1 for the camera's SECONDARY motion: the hit / recoil / landing kick, the opt-in shake,
 *     the sprint and dash FOV kick, the hovercraft's and the ship's chase lag and bank. Full 1, Reduced 0.35 (the
 *     sector's own step, `MOTION_SCALE`), Off 0. A headset is always 0 here — the head owns the camera (Meta: never move
 *     the view without the user's input), and the feel block is already skipped while presenting.
 *   · NOT scaled, on purpose: where the EYE is (the crouch / slide / seat eye height is the capsule's real height — a
 *     reduced-motion player still crouches under a slab), and anything that is aim, damage or threat information.
 *   · The SHAKE stays opt-in (XAG 117): `atlas:feel-shake = 'on'` is still the switch (the old flag keeps working — and
 *     now has a row), and the shake it allows is multiplied by the same scale.
 */
import { MOTION_SCALE, prefersReducedMotion } from './space_comfort.js';

type Store = Pick<Storage, 'getItem' | 'setItem'>;
function safeStorage(): Store | null { try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; } }

export type CameraMotion = 'system' | 'full' | 'reduced' | 'off';
export const CAMERA_MOTION_KEY = 'atlas:camera-motion';
/** The old opt-in shake flag (combat wave, 2026-09-04) — unchanged, now behind a row. */
export const FEEL_SHAKE_KEY = 'atlas:feel-shake';
export const CAMERA_MOTIONS: readonly CameraMotion[] = ['system', 'full', 'reduced', 'off'];

export function sanitizeCameraMotion(v: unknown): CameraMotion {
  return v === 'full' || v === 'reduced' || v === 'off' ? v : 'system';
}

let cached: CameraMotion | null = null;
/** The player's choice (cached; re-read with `resetCameraComfortCache`). */
export function cameraMotion(store: Store | null = safeStorage()): CameraMotion {
  if (cached) return cached;
  try { cached = sanitizeCameraMotion(store?.getItem(CAMERA_MOTION_KEY)); } catch { cached = 'system'; }
  return cached;
}
export function setCameraMotion(m: CameraMotion, store: Store | null = safeStorage()): CameraMotion {
  cached = sanitizeCameraMotion(m);
  try { store?.setItem(CAMERA_MOTION_KEY, cached); } catch { /* private window — the session keeps it */ }
  return cached;
}
/** Harnesses: forget the cached choice. */
export function resetCameraComfortCache(): void { cached = null; }

/** The opt-in shake switch (the old flag). */
export function shakeOptIn(store: Store | null = safeStorage()): boolean {
  try { return store?.getItem(FEEL_SHAKE_KEY) === 'on'; } catch { return false; }
}
export function setShakeOptIn(on: boolean, store: Store | null = safeStorage()): void {
  try { store?.setItem(FEEL_SHAKE_KEY, on ? 'on' : 'off'); } catch { /* private window */ }
}

export interface MotionInput { motion: CameraMotion; prefersReduced: boolean; presenting: boolean }
/** PURE: the scale for a choice, the OS preference and whether a headset presents. */
export function motionScaleFor(i: MotionInput): number {
  if (i.presenting) return 0;
  const m: 'full' | 'reduced' | 'off' = i.motion === 'system' ? (i.prefersReduced ? 'reduced' : 'full') : i.motion;
  return MOTION_SCALE[m];
}
/** The live scale (the stored choice, the OS media query, the session). */
export function cameraMotionScale(presenting: boolean, store: Store | null = safeStorage()): number {
  return motionScaleFor({ motion: cameraMotion(store), prefersReduced: prefersReducedMotion(), presenting });
}

/** The additive offsets the feel layer returns (`combat_feel.stepFeel`) — the eye drop is a POSITION and passes through. */
export interface AdditiveOffsets { pitch: number; yaw: number; roll: number; fov: number; eyeDrop: number }
/** PURE: scale the secondary motion by `s` (0..1); the eye drop is untouched. */
export function scaleOffsets<T extends AdditiveOffsets>(off: T, s: number): T {
  const k = Number.isFinite(s) ? Math.min(1, Math.max(0, s)) : 0;
  return { ...off, pitch: off.pitch * k, yaw: off.yaw * k, roll: off.roll * k, fov: off.fov * k };
}
